/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observable, action, runInAction, computed } from "mobx";

import type { DeploymentItem, TransferItem } from "@src/@types/MainItem";
import { getTransferItemTitle } from "@src/@types/MainItem";
import type { Execution } from "@src/@types/Execution";
import type { Task } from "@src/@types/Task";
import TransferSource from "@src/sources/TransferSource";
import DeploymentSource from "@src/sources/DeploymentSource";
import endpointStore from "@src/stores/EndpointStore";
import {
  ExecutionProgress,
  KnownDisk,
  isActiveStatus,
  isFailedStatus,
  parseApiDate,
  summariseExecution,
} from "@src/utils/TransferProgress";

/*
 * What is moving right now, for the Live monitor, the dashboard's "Live now" card and anything
 * else that wants it: every transfer and deployment of the project, and for the ones running (or
 * that finished in the last 24 h) their latest execution with its tasks, summarised by
 * TransferProgress. Polled every 5 s while something is watching (start/stop are counted).
 */

export const POLL_MS = 5000;
const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE = 200;
const MAX_PAGES = 10;
const MAX_SAMPLES = 36;

export type LiveKind = "replica" | "migration";

export type ThroughputSample = { t: number; rate: number; done: number };

export type LiveEntry = {
  /** The execution id (a deployment's id for deployments). */
  key: string;
  itemId: string;
  itemType: "transfer" | "deployment";
  kind: LiveKind;
  name: string;
  instances: string[];
  sourceEndpointId: string | null;
  targetEndpointId: string | null;
  projectId: string | null;
  executionNumber: number | null;
  status: string;
  createdAt: string;
  updatedAt: string | null;
  tasks: Task[];
  progress: ExecutionProgress;
  link: string;
};

/** A transfer's disks per instance, from the export info it keeps, for "waiting" disk rows. */
export const knownDisksOf = (item: TransferItem | DeploymentItem | null | undefined) => {
  const out: { [instance: string]: KnownDisk[] } = {};
  const info = (item && item.info) || {};
  Object.keys(info).forEach(instance => {
    const disks = (info[instance] as any)?.export_info?.devices?.disks;
    if (Array.isArray(disks)) {
      out[instance] = disks.map((d: any) => ({
        id: d.id != null ? String(d.id) : null,
        name: d.name || null,
        sizeBytes: typeof d.size_bytes === "number" ? d.size_bytes : null,
      }));
    }
  });
  return out;
};

/** Sum of the disk sizes a transfer knows about (its export info). */
export const knownSizeOf = (item: TransferItem | DeploymentItem | null | undefined) => {
  const disks = knownDisksOf(item);
  return Object.values(disks)
    .flat()
    .reduce((a, d) => a + (d.sizeBytes || 0), 0);
};

const kindOf = (scenario: string | null | undefined): LiveKind =>
  scenario === "live_migration" ? "migration" : "replica";

const recent = (iso: string | null | undefined, now: number) => {
  const t = parseApiDate(iso);
  return t != null && now - t < DAY_MS;
};

async function allPages<T extends { id: string }>(
  load: (marker: string | null) => Promise<T[]>,
): Promise<T[]> {
  const out: T[] = [];
  let marker: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const rows: T[] = await load(marker);
    out.push(...rows);
    if (rows.length < PAGE) {
      break;
    }
    marker = rows[rows.length - 1].id;
  }
  return out;
}

class LiveStore {
  @observable transfers: TransferItem[] = [];

  @observable deployments: DeploymentItem[] = [];

  @observable entries: LiveEntry[] = [];

  /** Throughput samples per entry key, oldest first. */
  @observable samples: { [key: string]: ThroughputSample[] } = {};

  /** Combined throughput of everything running, one sample per poll. */
  @observable totals: ThroughputSample[] = [];

  @observable loaded = false;

  @observable loading = false;

  @observable lastUpdated: number | null = null;

  private watchers = 0;

  private timer: number | null = null;

  private polling = false;

  private generation = 0;

  /** Finished executions never change: their tasks are kept. */
  private finishedCache = new Map<string, { status: string; tasks: Task[]; execution: Execution | null }>();

  @computed get running(): LiveEntry[] {
    return this.entries.filter(e => isActiveStatus(e.status));
  }

  @computed get recentlyFinished(): LiveEntry[] {
    return this.entries.filter(e => !isActiveStatus(e.status));
  }

  start() {
    this.watchers += 1;
    if (this.watchers === 1) {
      this.poll(true);
    }
  }

  stop() {
    this.watchers = Math.max(0, this.watchers - 1);
    if (this.watchers === 0 && this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** After a project change: forget everything and load again. */
  @action reset() {
    this.generation += 1;
    this.transfers = [];
    this.deployments = [];
    this.entries = [];
    this.samples = {};
    this.totals = [];
    this.loaded = false;
    this.finishedCache.clear();
    if (this.watchers > 0) {
      if (this.timer) {
        window.clearTimeout(this.timer);
      }
      this.polling = false;
      this.poll(true);
    }
  }

  private async poll(first = false) {
    if (this.polling) {
      return;
    }
    this.polling = true;
    const generation = this.generation;
    try {
      await this.refresh(first);
    } catch (err) {
      console.error(err);
    } finally {
      this.polling = false;
      if (this.watchers > 0 && generation === this.generation) {
        this.timer = window.setTimeout(() => this.poll(), POLL_MS);
      }
    }
  }

  async refresh(showLoading = false) {
    const generation = this.generation;
    if (showLoading) {
      runInAction(() => {
        this.loading = true;
      });
    }
    try {
      if (!endpointStore.endpoints.length && !endpointStore.loading) {
        endpointStore.getEndpoints({ skipLog: true }).catch(() => {});
      }
      const [transfers, deployments] = await Promise.all([
        allPages<TransferItem>(marker =>
          TransferSource.getTransfers({ skipLog: true, quietError: true, limit: PAGE, marker }),
        ),
        allPages<DeploymentItem>(marker =>
          DeploymentSource.getDeployments({ skipLog: true, quietError: true, limit: PAGE, marker }),
        ),
      ]);
      const now = Date.now();
      const wantedTransfers = transfers.filter(
        t =>
          isActiveStatus(t.last_execution_status) ||
          (t.last_execution_status !== "UNEXECUTED" && recent(t.updated_at, now)),
      );
      const wantedDeployments = deployments.filter(
        d => isActiveStatus(d.last_execution_status) || recent(d.updated_at || d.created_at, now),
      );

      const transferEntries = await Promise.all(
        wantedTransfers.map(t => this.transferEntry(t, now).catch(() => null)),
      );
      const deploymentEntries = await Promise.all(
        wantedDeployments.map(d => this.deploymentEntry(d, now).catch(() => null)),
      );
      if (generation !== this.generation) {
        return;
      }
      const entries = [...transferEntries, ...deploymentEntries].filter(
        (e): e is LiveEntry => Boolean(e),
      );
      // Running first (oldest start first), then what finished most recently.
      entries.sort((a, b) => {
        const aa = isActiveStatus(a.status) ? 0 : 1;
        const bb = isActiveStatus(b.status) ? 0 : 1;
        if (aa !== bb) {
          return aa - bb;
        }
        if (aa === 0) {
          return (parseApiDate(a.createdAt) || 0) - (parseApiDate(b.createdAt) || 0);
        }
        return (parseApiDate(b.updatedAt) || 0) - (parseApiDate(a.updatedAt) || 0);
      });
      runInAction(() => {
        this.transfers = transfers;
        this.deployments = deployments;
        this.entries = entries;
        const samples: { [key: string]: ThroughputSample[] } = {};
        entries.forEach(e => {
          const prev = this.samples[e.key] || [];
          if (!isActiveStatus(e.status)) {
            samples[e.key] = prev;
            return;
          }
          samples[e.key] = [
            ...prev,
            { t: now, rate: e.progress.rateBytesPerSec, done: e.progress.doneBytes },
          ].slice(-MAX_SAMPLES);
        });
        this.samples = samples;
        const running = entries.filter(e => isActiveStatus(e.status));
        this.totals = [
          ...this.totals,
          {
            t: now,
            rate: running.reduce((a, e) => a + e.progress.rateBytesPerSec, 0),
            done: running.reduce((a, e) => a + e.progress.doneBytes, 0),
          },
        ].slice(-MAX_SAMPLES);
        this.loaded = true;
        this.lastUpdated = now;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  private async transferEntry(t: TransferItem, now: number): Promise<LiveEntry | null> {
    const cached = this.finishedCache.get(t.id);
    let execution: Execution | null;
    let tasks: Task[];
    if (cached && cached.status === t.last_execution_status && !isActiveStatus(t.last_execution_status)) {
      execution = cached.execution;
      tasks = cached.tasks;
    } else {
      const [latest] = await TransferSource.getExecutions(t.id, {
        limit: 1,
        quietError: true,
        sortKeys: ["number"],
        sortDirs: ["desc"],
      });
      if (!latest) {
        return null;
      }
      const withTasks = await TransferSource.getExecutionTasks({
        transferId: t.id,
        executionId: latest.id,
        polling: true,
      });
      execution = latest;
      tasks = withTasks.tasks || [];
      if (!isActiveStatus(withTasks.status)) {
        this.finishedCache.set(t.id, { status: withTasks.status, tasks, execution: withTasks });
      }
      execution = { ...latest, status: withTasks.status, updated_at: withTasks.updated_at || latest.updated_at };
    }
    if (!execution) {
      return null;
    }
    if (!isActiveStatus(execution.status) && !recent(execution.updated_at || execution.created_at, now)) {
      return null;
    }
    const progress = summariseExecution(
      { status: execution.status, tasks, created_at: execution.created_at, updated_at: execution.updated_at, type: execution.type },
      { now, knownDisks: knownDisksOf(t) },
    );
    return {
      key: execution.id,
      itemId: t.id,
      itemType: "transfer",
      kind: kindOf(t.scenario),
      name: getTransferItemTitle(t) || t.id,
      instances: t.instances || [],
      sourceEndpointId: t.origin_endpoint_id || null,
      targetEndpointId: t.destination_endpoint_id || null,
      projectId: (t as any).project_id || null,
      executionNumber: execution.number,
      status: execution.status,
      createdAt: execution.created_at,
      updatedAt: execution.updated_at,
      tasks,
      progress,
      link: `/transfers/${t.id}/executions`,
    };
  }

  private async deploymentEntry(d: DeploymentItem, now: number): Promise<LiveEntry | null> {
    const cached = this.finishedCache.get(d.id);
    let tasks: Task[];
    let status = d.last_execution_status;
    let updatedAt = d.updated_at;
    if (cached && cached.status === d.last_execution_status && !isActiveStatus(status)) {
      tasks = cached.tasks;
    } else {
      const details = await DeploymentSource.getDeployment(d.id, true, true);
      tasks = details.tasks || [];
      status = details.last_execution_status || status;
      updatedAt = details.updated_at || updatedAt;
      if (!isActiveStatus(status)) {
        this.finishedCache.set(d.id, { status, tasks, execution: null });
      }
    }
    if (!isActiveStatus(status) && !recent(updatedAt || d.created_at, now)) {
      return null;
    }
    const progress = summariseExecution(
      { status, tasks, created_at: d.created_at, updated_at: updatedAt, type: "deployment" },
      { now },
    );
    return {
      key: d.id,
      itemId: d.id,
      itemType: "deployment",
      kind: kindOf(d.transfer_scenario_type),
      name: getTransferItemTitle(d) || d.id,
      instances: d.instances || [],
      sourceEndpointId: d.origin_endpoint_id || null,
      targetEndpointId: d.destination_endpoint_id || null,
      projectId: (d as any).project_id || null,
      executionNumber: null,
      status,
      createdAt: d.created_at,
      updatedAt: updatedAt || null,
      tasks,
      progress,
      link: `/deployments/${d.id}/tasks`,
    };
  }

  /** Executions that failed in the last 24 h, as far as the lists tell (latest run of each item). */
  failedLast24h(now = Date.now()) {
    const t = this.transfers.filter(
      x => isFailedStatus(x.last_execution_status) && recent(x.updated_at, now),
    ).length;
    const d = this.deployments.filter(
      x => isFailedStatus(x.last_execution_status) && recent(x.updated_at || x.created_at, now),
    ).length;
    return t + d;
  }
}

export default new LiveStore();
